import { useState } from 'react';
import { AdminPermissions } from '../types';
import { ADMIN_PERMISSION_FLAGS, ADMIN_PERMISSION_LIMITS, AdminPermissionLimit } from '../utils/adminPermissions';

// Matches the server's default: everything allowed, nothing capped. Used
// only if an Admin row somehow arrives without its permissions.
const ALL_ALLOWED: AdminPermissions = {
  resetPassword: true,
  disableAccount: true,
  deleteAccount: true,
  assignRoles: true,
  registerUser: true,
  renewalRequests: true,
  applicationReports: true,
  editSignatories: true,
  themes: true,
  maxPrograms: null,
  announcementsPerDay: null
};

interface AdminPermissionsEditorProps {
  initial?: AdminPermissions;
  onSave: (permissions: AdminPermissions) => Promise<void>;
  onCancel: () => void;
}

/**
 * The Super Admin's switches for one Admin. Edits a local draft and only
 * sends it on Save, so flicking through the switches doesn't fire a
 * request per click or leave the Admin half-changed if they cancel.
 */
const AdminPermissionsEditor = ({ initial, onSave, onCancel }: AdminPermissionsEditorProps) => {
  const [draft, setDraft] = useState<AdminPermissions>(initial ?? ALL_ALLOWED);
  // Limits are typed as text so the field can be emptied (= no limit)
  // without the input fighting back.
  const [limitText, setLimitText] = useState<Record<AdminPermissionLimit, string>>({
    maxPrograms: draft.maxPrograms === null ? '' : String(draft.maxPrograms),
    announcementsPerDay: draft.announcementsPerDay === null ? '' : String(draft.announcementsPerDay)
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    const limits: Partial<AdminPermissions> = {};
    for (const { key, label } of ADMIN_PERMISSION_LIMITS) {
      const text = limitText[key].trim();
      if (text === '') {
        limits[key] = null;
      } else if (/^\d+$/.test(text)) {
        limits[key] = parseInt(text, 10);
      } else {
        setError(`${label} must be a whole number, or empty for no limit.`);
        return;
      }
    }

    setSaving(true);
    try {
      await onSave({ ...draft, ...limits });
    } catch (err: any) {
      setError(err.message || 'Failed to save permissions');
      setSaving(false);
    }
  };

  const allOn = ADMIN_PERMISSION_FLAGS.every(({ key }) => draft[key]);

  return (
    <div>
      {error && (
        <p className="alert-error" style={{ padding: '0.75rem', marginBottom: '1rem' }}>
          {error}
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <strong>Access</strong>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          disabled={saving}
          onClick={() =>
            setDraft((prev) => {
              const next = { ...prev };
              for (const { key } of ADMIN_PERMISSION_FLAGS) next[key] = !allOn;
              return next;
            })
          }
        >
          {allOn ? 'Disable all' : 'Enable all'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1.25rem' }}>
        {ADMIN_PERMISSION_FLAGS.map(({ key, label, description }) => (
          <label
            key={key}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.6rem',
              padding: '0.5rem 0.65rem',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer'
            }}
          >
            <input
              type="checkbox"
              checked={draft[key]}
              disabled={saving}
              onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.checked }))}
              style={{ marginTop: '0.2rem' }}
            />
            <span style={{ flex: 1 }}>
              <span style={{ fontWeight: 600 }}>{label}</span>
              <span style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{description}</span>
            </span>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: draft[key] ? '#34D399' : 'var(--text-secondary)' }}>
              {draft[key] ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        ))}
      </div>

      <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Limits</strong>
      {ADMIN_PERMISSION_LIMITS.map(({ key, label, description }) => (
        <div className="form-group" key={key}>
          <label htmlFor={`limit-${key}`}>{label}</label>
          <input
            id={`limit-${key}`}
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            placeholder="No limit"
            value={limitText[key]}
            disabled={saving}
            onChange={(e) => setLimitText((prev) => ({ ...prev, [key]: e.target.value }))}
          />
          <small style={{ color: 'var(--text-secondary)' }}>{description}</small>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
        <button className="btn btn-primary" type="button" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving...' : 'Save Permissions'}
        </button>
        <button className="btn btn-outline" type="button" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AdminPermissionsEditor;
